import { Vehicle } from '../types/vehicle';

/**
 * 자금원 종류와 VICS 보고 주기 (2026-10-26) — 서버 `backend/src/modules/compliance/rules.ts` 와 같은 규칙.
 *
 *   · 개인투자조합 — 반기 보고. 상반기분 7/1~31 · 하반기분 1/1~31 (고시 2023-28호 12조). 1316 재무는 의무가 아니다
 *   · 창업기획자(고유계정) — 매월 1~7일 업무운용상황 보고 (관리규정 8조)
 *   · 벤처투자조합 — 월보고로 둔다. 주기는 문서로 확인하지 못했다
 */
export type FundKind = 'ac' | 'personal' | 'venture' | 'other';

export function fundKind(v: Pick<Vehicle, 'kind' | 'vehicleType'>): FundKind {
  if (v.kind === 'own') return 'ac';
  const t = v.vehicleType ?? '';
  if (t.includes('개인')) return 'personal';
  if (t.includes('벤처')) return 'venture';
  return 'other';
}

export const FUND_KIND_LABEL: Record<FundKind, string> = {
  ac: '창업기획자', personal: '개인투자조합', venture: '벤처투자조합', other: '기타',
};

export type Cadence = 'half' | 'month';
export const vicsCadence = (v: Pick<Vehicle, 'kind' | 'vehicleType'>): Cadence => (fundKind(v) === 'personal' ? 'half' : 'month');

/** 보고 기간 키 — 월보고는 그 달(2026-09), 반기 보고는 기간 말 월(2026-06 · 2026-12) */
export function periodMonths(key: string, cadence: Cadence): string[] {
  if (cadence === 'month') return [key];
  const y = key.slice(0, 4);
  const first = key.endsWith('-06') ? 1 : 7;
  return Array.from({ length: 6 }, (_, i) => `${y}-${String(first + i).padStart(2, '0')}`);
}

export const periodLabel = (key: string, cadence: Cadence) =>
  cadence === 'month' ? `${key.slice(0, 4)}년 ${key.slice(5, 7)}월` : `${key.slice(0, 4)}년 ${key.endsWith('-06') ? '상반기' : '하반기'}`;

/** 지금 낼 기간 — 월보고는 지난달, 반기 보고는 끝난 지 가장 가까운 반기 */
export function currentPeriod(cadence: Cadence, today: Date = new Date()): string {
  const y = today.getFullYear(), m = today.getMonth() + 1;
  if (cadence === 'month') {
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  return m >= 7 ? `${y}-06` : `${y - 1}-12`;
}

/** 재무(1316)를 볼 사업연도 — 기간이 속한 해의 전년 */
export const financialYear = (key: string) => Number(key.slice(0, 4)) - 1;

/* ── 서버 응답 ── */
export interface DueItem {
  vehicleId: number; vehicleName: string; key: string; title: string;
  dueOn: string; left: number; basis: string; link: string; unsure?: boolean;
}
export interface RatioCheck {
  vehicleId: number | null; vehicleName: string; key: string; title: string;
  op: 'min' | 'max'; threshold: number; ratio: number | null;
  numerator: number; base: number; unknown: number;
  deadline: string | null; status: 'ok' | 'pending' | 'short' | 'over' | 'unknown';
  basis: string; note?: string;
}
export interface ComplianceOverview { today: string; due: DueItem[]; checks: RatioCheck[] }

export const CHECK_STATUS: Record<RatioCheck['status'], { label: string; style: string }> = {
  ok: { label: '충족', style: 'bg-emerald-50 text-emerald-700' },
  pending: { label: '기한 전 미달', style: 'bg-amber-50 text-amber-700' },
  short: { label: '미달', style: 'bg-red-50 text-red-700' },
  over: { label: '초과', style: 'bg-red-50 text-red-700' },
  unknown: { label: '확인 필요', style: 'bg-slate-100 text-slate-600' },
};

export const leftLabel = (left: number) => (left > 0 ? `D-${left}` : left === 0 ? '오늘' : `${-left}일 지남`);
